<script setup lang="ts">
import { basename } from 'pathe'

const props = defineProps<{ source: string }>()
const isMobileScreen = useIsMobileScreen()

const { emailParser } = useQuery()

const fileType = computed(() => {
  const ext = getFileExtension(props.source)
  if (ext !== '.eml' && ext !== '.msg') {
    return null
  }
  return ext
})

const { data: emailData, isLoading } = emailParser.byFilePath(props.source, fileType.value)

const formattedRecipients = computed(() => {
  return emailData.value?.recipients.map((recipient: any) => {
    return `${recipient.name} <${recipient.email}>`
  }).join(', ')
})

const formattedDate = computed(() => {
  if (!emailData.value?.date) {
    return undefined
  }

  return useDateAsString(emailData.value.date, 'dd.MM.yyyy, HH:mm:ss')
})

function downloadAttachment(content: string, fileName: string, contentType?: string) {
  // Decode base64 content
  const binaryString = atob(content)
  const bytes = new Uint8Array(binaryString.length)
  for (let index = 0; index < binaryString.length; index++) {
    const codePoint = binaryString.codePointAt(index)
    if (codePoint === undefined) {
      continue
    }
    bytes[index] = codePoint
  }

  const blob = new Blob([bytes], { type: contentType ?? 'application/octet-stream' })
  const url = URL.createObjectURL(blob)

  // Create a temporary link and trigger download
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.append(link)
  link.click()
  link.remove()

  // Clean up the blob URL
  URL.revokeObjectURL(url)
}
</script>

<template>
  <ThePopup :show="!!source" :width="isMobileScreen ? '100%' : '70%'" :title="$t('emailViewer.title', { filename: basename(props.source) })">
    <div v-if="isLoading" class="h-[30vh] w-full flex items-center justify-center">
      <n-spin size="large" />
      <span class="pt-20"> {{ $t('emailViewer.loading') }}</span>
    </div>
    <div v-else-if="!!emailData">
      <div class="pb-4">
        <h1 class="Subject pb-4">
          {{ emailData.subject }}
        </h1>
        <div class="p-2 border border-gray-200 rounded-md">
          <div class="flex gap-2">
            <span class="MetaInformation">{{ $t('emailViewer.field.from') }}</span>
            <span>{{ emailData.sender.name }} &lt;{{ emailData.sender.email }}&gt; </span>
          </div>
          <div v-if="formattedRecipients" class="flex gap-2">
            <span class="MetaInformation">{{ $t('emailViewer.field.to') }}</span>
            <span>{{ formattedRecipients }}</span>
          </div>
          <div v-if="emailData.date" class="flex gap-2">
            <span class="MetaInformation">{{ $t('emailViewer.field.date') }}</span>
            <span>{{ formattedDate }}</span>
          </div>
        </div>
      </div>

      <div class="pb-4">
        <h2 class="font-semibold text-gray-800 pb-1">
          {{ $t('emailViewer.field.attachments') }}
        </h2>
        <div class="flex gap-2 flex-wrap">
          <template v-for="attachment in emailData.attachments" :key="attachment.fileName">
            <div v-if="attachment.content && attachment.fileName">
              <n-button @click="downloadAttachment(attachment.content, attachment.fileName, attachment.contentType)">
                <template #icon>
                  <Icon name="material-symbols:attach-file" class="text-hos-blue" />
                </template>
                {{ attachment.fileName }}
              </n-button>
            </div>
          </template>
        </div>
      </div>

      <div class="pl-4 py-2 border-l border-gray-200">
        <pre v-if="emailData.data.text" class="whitespace-pre-wrap font-sans">{{ emailData.data.text }}</pre>
      </div>
    </div>
  </ThePopup>
</template>

<style scoped>
.Subject {
  @apply text-lg text-[#262626];
  font-weight: 500;
}

.MetaInformation {
  @apply text-gray-500 whitespace-nowrap flex-nowrap min-w-[3rem];
}
</style>
